import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { PLATFORMS } from '../data/content';
import SEO from '../components/SEO';

const Platforms = () => {
  return (
    <div className="bg-[#050505] text-white" data-testid="platforms-page">
      <SEO
        title="Platforms — BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos"
        description="Explore the platforms built by Bronto Intelligence: BrontoAI for general intelligence, MSPConnect for agricultural intelligence, and Bronto Cosmos for space and astronomy discovery."
        path="/platforms"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Platforms
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            Platforms within the <span className="rainbow-text">Bronto ecosystem.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-4xl leading-relaxed font-light">
            BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos are the flagship technology platforms developed and operated within the Bronto ecosystem. Each platform serves a distinct purpose and audience while sharing the company&apos;s commitment to accessible, well-designed digital experiences.
          </p>
        </div>
      </section>

      {/* Platforms */}
      <section className="px-6 lg:px-10 pb-24 border-t border-white/5 pt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          {PLATFORMS.map((platform, idx) => (
            <div
              key={platform.id}
              className="group relative rounded-3xl glass hover-lift overflow-hidden"
              data-testid={`platform-detail-${platform.id}`}
            >
              <div
                className={`absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-gradient-to-br ${platform.gradient}`}
              />

              <div className="relative z-10 grid md:grid-cols-12 gap-0 p-10 md:p-14">
                <div className="md:col-span-7">
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                    0{idx + 1} · {platform.category}
                  </div>
                  <h2 className={`text-5xl md:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r ${platform.gradient} bg-clip-text text-transparent`}>
                    {platform.name}
                  </h2>
                  <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mb-10">
                    {platform.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4">
                    <Link to={`/${platform.id}`} className="btn-primary">
                      Explore {platform.name}
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <a
                      href={platform.appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                    >
                      {platform.id === 'brontocosmos' ? 'Visit' : 'App Store'}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>

                <div className="md:col-span-5 mt-10 md:mt-0">
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-5">
                    Capabilities
                  </div>
                  <ul className="space-y-3">
                    {platform.capabilities.map((cap) => (
                      <li key={cap} className="flex items-start gap-3">
                        <span className="w-1 h-1 rounded-full bg-white/40 mt-2.5 shrink-0" />
                        <span className="text-sm text-white/80">{cap}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem Note */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Ecosystem
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            One company. Multiple platforms. Long-term focus.
          </h2>
          <p className="text-base md:text-lg text-white/60 leading-relaxed">
            Bronto Intelligence Private Limited is the parent organization behind BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos. Bronto Platforms Private Limited and Bronto Cosmos Private Limited serve as the operating subsidiaries responsible for managing the company&apos;s digital platforms.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Platforms;
